/**
 * MCP Resource
 *
 * Thin 1:1 mapping to the MCP and tools API. No business logic, no default
 * parameter injection. The SDK passes params as-is.
 */

import type { HttpLayer } from '../http.js';
import type {
  MCPServer,
  AppRegisterMCPRequest,
  AppUpdateMCPRequest,
  MCPTool,
  MCPToolCatalog,
} from '../types/index.js';

export class MCPResource {
  constructor(private http: HttpLayer) {}

  // --- Device-scoped MCP endpoints ---

  /**
   * Register an MCP server for a device. A remote server (`http`) is stored with
   * its `url`. A local (`bridge`) registration is the computer's own report of a
   * server it already runs, with an empty `config`; local servers are added from
   * the ShogunChop desktop app. A `bridge` registration whose `config` carries
   * `command`, `args` or `env` is refused (`error_code:
   * local_server_definition_refused`, HTTP 422), and one the connected computer
   * says it does not run is refused (`error_code: not_running_on_device`, HTTP
   * 409). A refused registration stores nothing.
   */
  async register(
    deviceId: string,
    params: AppRegisterMCPRequest,
  ): Promise<MCPServer> {
    return this.http.post(
      `/v1/devices/${deviceId}/mcp`,
      params,
    );
  }

  async list(deviceId: string): Promise<MCPServer[]> {
    return this.http.get(
      `/v1/devices/${deviceId}/mcp`,
    );
  }

  async update(
    deviceId: string,
    mcpId: string,
    params: AppUpdateMCPRequest,
  ): Promise<MCPServer> {
    return this.http.put(
      `/v1/devices/${deviceId}/mcp/${mcpId}`,
      params,
    );
  }

  async delete(
    deviceId: string,
    mcpId: string,
  ): Promise<MCPServer> {
    return this.http.delete(
      `/v1/devices/${deviceId}/mcp/${mcpId}`,
    );
  }

  async getToolCatalog(deviceId: string): Promise<MCPToolCatalog> {
    return this.http.get(
      `/v1/devices/${deviceId}/mcp/catalog`,
    );
  }

  async listTools(
    deviceId: string,
    mcpId: string,
  ): Promise<MCPTool[]> {
    return this.http.get(
      `/v1/devices/${deviceId}/mcp/${mcpId}/tools`,
    );
  }

  // --- Tenant-scoped tool endpoints ---

  async listTenantMCPs(): Promise<MCPServer[]> {
    return this.http.get('/v1/tools/mcps');
  }

  async listTenantMCPTools(mcpId: string): Promise<MCPTool[]> {
    return this.http.get(`/v1/tools/mcps/${mcpId}/tools`);
  }

  async getTenantToolCatalog(): Promise<MCPToolCatalog> {
    return this.http.get('/v1/tools/catalog');
  }
}
